import { accountColorSchema, categoryIconSchema, parseApiResponse, tagBalanceSchema, tagSchema } from "@/api/responseSchemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useAuthStore } from "@/stores/useAuthStore";
import { captureEntityMutation } from "@/analytics/posthog";

export interface Tag {
  id: number;
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
  type?: string;
}

export interface TagBalance {
  id: number;
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
  tag_total_expense: number;
  tag_total_deposit: number;
  percentage_of_total_expense: number;
  percentage_of_total_deposit: number;
  /** Percentage over tagged-only total; values across all tags sum to 100%. Absent on API builds prior to hj-api#82. */
  percentage_of_tagged_expense?: number;
}

export interface TagBalanceResponse {
  total_deposit: number;
  total_expense: number;
  count: number;
  tags_balance: TagBalance[];
  /** Spend with no tag, or whose tag was deleted. Already inside the totals. */
  untagged_total_expense: number;
  untagged_total_deposit: number;
  /** total_expense − untagged_total_expense; use as sidebar display value. Absent on API builds prior to hj-api#82. */
  tagged_total_expense?: number;
}

export interface TagInput {
  name: string;
  description?: string;
  color_id?: number | null;
  icon_id?: number | null;
}

interface TagsBalanceQueryParams {
  billable_date_gte?: string;
  billable_date_lte?: string;
  tag_total_expense?: "asc" | "desc" | string;
}

export const useTagColors = (options?: { enabled?: boolean }) => {
  const token = useAuthStore((state) => state.token);
  return useQuery({
    queryKey: ["tagColors"],
    queryFn: async () => {
      const response = await apiClient.get("/tags/colors");
      return parseApiResponse(accountColorSchema.array(), response.data, "GET /tags/colors");
    },
    enabled: !!token && (options?.enabled ?? true),
    staleTime: Infinity,
  });
};

export const useTagIcons = (options?: { enabled?: boolean }) => {
  const token = useAuthStore((state) => state.token);
  return useQuery({
    queryKey: ["tagIcons"],
    queryFn: async () => {
      const response = await apiClient.get("/tags/icons");
      return parseApiResponse(categoryIconSchema.array(), response.data, "GET /tags/icons");
    },
    enabled: !!token && (options?.enabled ?? true),
    staleTime: Infinity,
  });
};

export const useTagsBalance = (params?: TagsBalanceQueryParams) => {
  const token = useAuthStore((s) => s.token);
  const effectiveParams: TagsBalanceQueryParams = {
    tag_total_expense: params?.tag_total_expense ?? "desc",
    ...(params?.billable_date_gte ? { billable_date_gte: params.billable_date_gte } : {}),
    ...(params?.billable_date_lte ? { billable_date_lte: params.billable_date_lte } : {}),
  };

  return useQuery({
    queryKey: ["tagsBalance", effectiveParams],
    queryFn: async () => {
      const res = await apiClient.get("/tags/balance", {
        params: effectiveParams,
      });
      return parseApiResponse(tagBalanceSchema, res.data, "GET /tags/balance");
    },
    enabled: !!token,
  });
};

export const useTags = (options?: { enabled?: boolean }) => {
  const token = useAuthStore((s) => s.token);

  return useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await apiClient.get("/tags/");
      return parseApiResponse(tagSchema.array(), res.data, "GET /tags");
    },
    enabled: !!token && (options?.enabled ?? true),
  });
};

export const useCreateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagData: TagInput) => {
      const dataToSend = {
        name: tagData.name,
        ...(tagData.description !== undefined && { description: tagData.description }),
        ...(tagData.color_id !== undefined && { color_id: tagData.color_id }),
        ...(tagData.icon_id !== undefined && { icon_id: tagData.icon_id }),
      };
      const res = await apiClient.post("/tags/", dataToSend);
      return res.data;
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "tag", operation: "create", result: "success" });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["tagsBalance"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "tag", operation: "create", result: "failure", error });
    },
  });
};

export const useUpdateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: TagInput }) => {
      const dataToSend = {
        name: data.name,
        ...(data.description !== undefined && { description: data.description }),
        ...(data.color_id !== undefined && { color_id: data.color_id }),
        ...(data.icon_id !== undefined && { icon_id: data.icon_id }),
      };
      const res = await apiClient.patch(`/tags/${id}`, dataToSend);
      return res.data;
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "tag", operation: "update", result: "success" });
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["tagsBalance"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "tag", operation: "update", result: "failure", error });
    },
  });
};

export const useDeleteTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagId: number) => {
      const res = await apiClient.delete(`/tags/${tagId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["tagsBalance"] });
    },
  });
};
